// MACHINERY V2 (Stroje) — V3's PHONE row (M2.16b).
//
// Rendered truth: mobile/screenshots/10-v3-phone.png, viewed before this file
// was written. Reference impl: mobile/machinery-mobile.jsx MbV3Phone's inner row
// (:304-321); values verbatim — `minHeight: 56`, padding 10/12, gap 9, a 14px
// alarm corner triangle, name 13/600, mono sub 9.5, then sparkline · status chip
// · open-SR pill.
//
// >>> NO HORIZONTAL SCROLL, EVER. <<< The whole reason this component exists
// instead of the `.ms-table` is the package's hard rule for V3 on a phone. The
// table's fixed columns total 824px before STROJ gets anything (FleetTable's
// header), so on a 390px screen it can only be a side-scroller — and a dense
// index you have to drag sideways to read is not an index.
//
// >>> IT HAS NO FOCUSABLE DESCENDANT, ON PURPOSE. <<< The three marks that carry
// extra information — the alarm triangle, the sparkline placeholder and the SR
// pill — are `title`-bearing spans, exactly as they are in the desktop table.
// None of them is a button, none takes focus, none fires an activation event, so
// the row's own tap cannot be swallowed and this file needs no propagation
// guard. (Contrast FleetCardCompact, which DOES embed a kebab and therefore
// relies on FleetCardMenu's click + Enter/Space guards.) The kebab is absent
// here for the reason the board omits it: a legal 44px trigger inside a 56px row
// takes 11% of a 390px viewport away from the machine name, and every action it
// would open is on the V1 card at this width and on the profile this row taps
// through to.
//
// SINGLE DERIVATION, AND THE SUB-LINE IS NOW STRUCTURALLY THE TABLE'S. The
// presentation object is built ONCE by the list and handed down, and the
// sub-line comes from deriveFleetActivity() (fleetActivity.ts) — the SAME call
// the V3 table's AKTUÁLNE cell, the V1 compact card's footer and the V4 status
// row make. It used to MIRROR that branch order by hand, which is how the four
// M2.16b surfaces drifted apart while each of their headers asserted they could
// not (review finding F6). What is still this row's own is the REGISTER, passed
// as options: the alarm prints its code (this line is the only place the code
// appears here), and there is no no-agent branch — at 390px the explainer is
// wider than the machine name above it, so the serial is the more useful line
// and it is now an explicit `fallback`, not an omitted branch.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { getMachineDisplayName } from "../../../../../shared/utils/machineUtils";
import { Machine } from "../../../models/models";
import StatusBadge, { AwaitingBadge } from "../StatusBadge";
import { MachineryV2CardPresentation, MachineryV2Telemetry } from "../cardStates";
import {
  cardStateLabelKey,
  deriveFleetActivity,
  fleetActivityLine,
  showsStatusChip,
} from "../fleetActivity";
import { NewBadge } from "../fleetCardAtoms";
import { SparklineCell, SrCell } from "./fleetTableDataCells";

export interface FleetPhoneRowProps {
  machine: Machine;
  presentation: MachineryV2CardPresentation;
  telemetry: MachineryV2Telemetry;
  isNew: boolean;
  /** False on the first row of a section — the card's own border is the rule. */
  divider: boolean;
  onOpenProfile: () => void;
}

const FleetPhoneRow: React.FC<FleetPhoneRowProps> = ({
  machine, presentation: p, telemetry: t, isNew, divider, onOpenProfile,
}) => {
  const { translate } = useTranslation("myMachinery");
  // ONE derivation of "what is this machine doing", shared with the table, the
  // card and the wall. `showAlarmStrip` already folds in `alarms.length > 0`
  // (cardStates.ts:277), so the re-test this row used to carry is gone rather
  // than moved — the very thing this file's header forbids (finding F11).
  const activity = deriveFleetActivity(p, t);
  const isAlarm = activity.kind === "alarm";

  // The board's four-way sub-line, over what our wire actually carries. The
  // job-description branch it draws has no field on the fleet DTO (see
  // fleetTableDataCells.tsx's CurrentCell note), so freshness takes its place
  // and the serial is the floor — never a fabricated string.
  const sub = fleetActivityLine(activity, {
    alarmCode: true,
    noTelemetry: null,
    fallback: machine.serialNumber ? "S/N " + machine.serialNumber : "",
  });

  return (
    <div
      onClick={onOpenProfile}
      data-machine-id={machine.id}
      style={{
        display: "flex", alignItems: "center", gap: 9, padding: "10px 12px",
        position: "relative", minHeight: 56, minWidth: 0, cursor: "pointer",
        borderTop: divider ? "1px solid var(--border-soft)" : "none",
        opacity: machine.isDecommissioned ? 0.65 : 1,
      }}
    >
      {/* The alarm corner triangle — 14px here where the table cell uses 16
          (fleetTableCells.tsx:74-91, re-read while writing this line), because
          the row is 56px rather than a two-line table cell. SAME SEMANTICS:
          absolutely positioned, so it steals no column width, and `title`-only,
          so it adds no focus stop to a row that is itself the tap target.
          >>> AND IT CARRIES THE "!", which it shipped without (finding F8). <<<
          The table's mark nests one (fleetTableCells.tsx:82-89) for the reason
          §4.3 makes every status carry an icon beside its colour, and
          StatusBadge's StaleMark repeats: a red wedge with nothing in it encodes
          "alarm" in HUE ALONE, which is exactly what a colour-blind operator
          cannot read. Geometry: the parent is a 0x0 box whose 14px top border
          draws the wedge, so its padding box — the child's containing block —
          starts 14px down; `top: -13` lands the glyph 1px from the row's top
          edge, inside the widest part of the wedge. Same construction as the
          table's `top: -15` under a 16px border, scaled. */}
      {activity.kind === "alarm" && (
        <span
          title={activity.alarm.code + ": " + activity.alarm.message}
          style={{
            position: "absolute", top: 0, left: 0, width: 0, height: 0,
            borderTop: "14px solid var(--red)", borderRight: "14px solid transparent",
          }}
        >
          <span
            style={{
              position: "absolute", top: -13, left: 2, color: "#fff", fontSize: 8,
              fontFamily: "var(--font-mono)", fontWeight: 700, lineHeight: 1,
            }}
          >
            !
          </span>
        </span>
      )}

      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span
            style={{
              fontSize: 13, fontWeight: 600, color: "var(--ink)", minWidth: 0,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}
          >
            {getMachineDisplayName(machine)}
          </span>
          {isNew && <NewBadge />}
        </div>
        {sub && (
          <div
            style={{
              fontFamily: "var(--font-mono)", fontSize: 9.5, marginTop: 2,
              color: isAlarm ? "var(--red)" : "var(--faint)",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}
          >
            {sub}
          </div>
        )}
      </div>

      {/* THE SAME data-gated component the 30 DNÍ column renders — one dash and
          one "why" tooltip, not a second placeholder. The board draws 30 bars;
          the prototype generates them from Math.sin(id.charCodeAt(1)) and we
          have no per-day series on the wire, so drawing them here would be
          decoration presented as history. See fleetTableDataCells.tsx. */}
      <SparklineCell />

      {/* THE SAME predicate StatusCell uses — literally, now: one exported call
          instead of two hand-kept copies of the expression (fleetActivity.ts).
          `showStatusRow` alone does not promise a visible chip, because a
          status-less `stale` machine has it true and renders nothing. The
          tooltip names THAT state rather than calling every dash unconnected. */}
      {showsStatusChip(p) ? (
        p.showAwaitingBadge ? (
          <AwaitingBadge />
        ) : (
          <StatusBadge status={p.badgeStatus} stale={p.staleLook} variant="chip" />
        )
      ) : (
        <span
          title={translate(cardStateLabelKey(p.state))}
          style={{
            fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--faint)",
            flexShrink: 0,
          }}
        >
          —
        </span>
      )}

      {/* The open-SR pill, reused verbatim from the table so the alarm-row fill
          and the 0 / 1-2 / 3+ health ramp stay ONE rule (fleetTableDataCells'
          SrCell) rather than a second pill with a second colour policy. At zero
          it degrades to the same mono dash the table prints — which is what the
          board's last row draws. */}
      <SrCell machine={machine} presentation={p} />
    </div>
  );
};

export default FleetPhoneRow;
